import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

type SectionHeaderProps = {
  eyebrow?: ReactNode;
  /** The plain part of the heading. */
  title: ReactNode;
  /** The one italic wine phrase; after the title unless `accentFirst`. */
  accent?: ReactNode;
  accentFirst?: boolean;
  lead?: ReactNode;
  align?: "center" | "left";
  as?: "h2" | "h3";
  id?: string;
  className?: string;
};

/**
 * The home page's section header, for every page: a grey eyebrow, a Playfair
 * heading in normal weight with ONE italic wine accent, and an optional lead.
 * Centred like on the home page; `align="left"` only for a heading that sits
 * in a column next to other content.
 */
export function SectionHeader({
  eyebrow,
  title,
  accent,
  accentFirst = false,
  lead,
  align = "center",
  as: Tag = "h2",
  id,
  className,
}: SectionHeaderProps) {
  const accentNode = accent ? <span className="italic font-normal text-danish-700">{accent}</span> : null;
  return (
    <div className={cn(align === "center" ? "mx-auto max-w-3xl text-center" : "max-w-2xl", className)}>
      {eyebrow ? <span className="eyebrow">{eyebrow}</span> : null}
      <Tag
        id={id}
        className={cn(
          "text-balance font-display text-[1.5rem] font-normal leading-[1.12] text-ink-900 sm:text-[1.9rem]",
          eyebrow ? "mt-3" : null,
        )}
      >
        {accentFirst && accentNode ? (
          <>
            {accentNode} {title}
          </>
        ) : (
          <>
            {title}
            {accentNode ? <> {accentNode}</> : null}
          </>
        )}
      </Tag>
      {lead ? <p className={cn("section-lead", align === "center" && "mx-auto")}>{lead}</p> : null}
    </div>
  );
}
